import React from 'react';
import { useTranslation } from 'react-i18next';
import { colors } from '@guryou/colors';

import CategoryTag from './CategoryTag';
import ImageWithFallback from './ImageWithFallback';

const ProviderCard = ({
  provider,
  type = 'center', // 'center' or 'professional'
  onViewProfile,
  navigationType = 'modal', // 'modal' or 'navigation'
  className = '',
}) => {
  const { t } = useTranslation();

  if (type === 'professional') {
    return (
      <div className={`group text-center relative p-6 pb-20 ${className}`}>
        <div className="relative mb-6">
          <div
            className="w-32 h-32 mx-auto rounded-full overflow-hidden ring-4 ring-white shadow-lg group-hover:ring-4 group-hover:ring-opacity-50 transition-all duration-300"
            style={{ ringColor: colors.mandy }}
          >
            <ImageWithFallback
              src={provider.images?.[0]?.medium}
              alt={provider.name}
              className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
              fallbackIcon={
                <svg className="w-8 h-8 opacity-50" style={{ color: colors.mineShaft }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1.5}
                    d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                  />
                </svg>
              }
            />
          </div>
        </div>

        <h3 className="text-lg font-bold mb-2 group-hover:transition-colors duration-200">
          <a href="/" className={`block text-[${colors.mineShaft}] hover:text-[${colors.mandy}]`}>
            {provider.name}
          </a>
        </h3>

        <div className="flex flex-wrap gap-2 justify-center mb-4">
          {provider?.categories?.slice(0, 2).map((cat, index) => (
            <CategoryTag key={index} name={cat.name} size="xs" variant="secondary" />
          ))}
        </div>

        {/* Absolute positioned button */}
        <div className="absolute bottom-0 left-0 right-0 p-4">
          <button
            className={`w-full px-4 py-2 rounded-full text-xs font-semibold transition-all duration-200 hover:scale-105 border-2 border-[${colors.mandy}] text-[${colors.mandy}] bg-transparent hover:bg-[${colors.mandy}] hover:text-white`}
            onClick={() => {
              if (navigationType === 'navigation') {
                window.location.href = `/it/provider/${provider.id}`;
              } else {
                onViewProfile(provider);
              }
            }}
          >
            {t('BOOK_SECTION.viewProfile')}
          </button>
        </div>
      </div>
    );
  }

  // Center card
  return (
    <div
      className={`group bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 overflow-hidden border border-gray-100 hover:border-gray-200 relative ${className}`}
    >
      <div className="relative overflow-hidden">
        <ImageWithFallback
          src={provider.images?.[0]?.medium}
          alt={provider.name}
          className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-300"
          fallbackIcon={
            <svg className="w-12 h-12 mb-3 opacity-50" style={{ color: colors.mineShaft }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
              />
            </svg>
          }
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
      </div>

      <div className="p-4 pb-16">
        <div className="flex flex-wrap gap-2 mb-3">
          {provider?.categories?.slice(0, 3).map((cat, index) => (
            <CategoryTag key={index} name={cat.name} size="sm" variant="default" />
          ))}
        </div>

        <h3 className="text-lg font-bold mb-2 group-hover:transition-colors duration-200">
          <a href="/" className={`block text-[${colors.mineShaft}] hover:text-[${colors.mandy}]`}>
            {provider.name}
          </a>
        </h3>

        <p className="text-gray-600 text-sm leading-relaxed line-clamp-3 mb-3">{provider.description}</p>
      </div>

      {/* Absolute positioned button */}
      <div className="absolute bottom-0 left-0 right-0 p-4">
        <button
          className="w-full px-4 py-2 rounded-full text-xs font-semibold transition-all duration-200 hover:scale-105 border-2"
          style={{
            borderColor: colors.mandy,
            color: colors.mandy,
            backgroundColor: 'transparent',
          }}
          onMouseEnter={e => {
            e.target.style.backgroundColor = colors.mandy;
            e.target.style.color = colors.white;
          }}
          onMouseLeave={e => {
            e.target.style.backgroundColor = 'transparent';
            e.target.style.color = colors.mandy;
          }}
          onClick={() => {
            if (navigationType === 'navigation') {
              window.location.href = `/it/provider/${provider.id}`;
            } else {
              onViewProfile(provider);
            }
          }}
        >
          {t('BOOK_SECTION.viewProfile')}
        </button>
      </div>
    </div>
  );
};

export default ProviderCard;
